import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '../supabaseClient';

export function useRealTime() {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const sessionRef = useRef(null);
  const cleanupRef = useRef([]);
  const heartbeatRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const fetchOnlineUsers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('is_online', true)
        .order('last_seen', { ascending: false });

      if (error) throw error;
      setOnlineUsers(data || []);
    } catch (error) {
      console.error('Error fetching online users:', error);
    }
  }, []);

  const fetchRecentActivities = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setRecentActivities(data || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  }, []);

  // Enhanced heartbeat function to maintain presence
  const sendHeartbeat = useCallback(async () => {
    if (!sessionRef.current) return;

    try {
      await supabase
        .from('user_sessions')
        .update({ 
          last_seen: new Date().toISOString(),
          is_online: true
        })
        .eq('user_id', sessionRef.current.user_id);
    } catch (error) {
      console.error('Error sending heartbeat:', error);
    }
  }, []);

  // Start heartbeat interval
  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
    }

    // Send heartbeat every 30 seconds
    heartbeatRef.current = setInterval(sendHeartbeat, 30000);
  }, [sendHeartbeat]);

  // Stop heartbeat interval
  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const setupSubscriptions = useCallback(() => {
    console.log('🔗 Setting up real-time subscriptions...');

    // Subscribe to user sessions (presence)
    const sessionsSubscription = supabase
      .channel('user_sessions_realtime')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'user_sessions' },
        (payload) => {
          console.log('👥 User session change:', payload);
          fetchOnlineUsers();
        }
      )
      .subscribe((status) => {
        console.log('👥 Sessions subscription status:', status);
      });

    // Subscribe to activities with enhanced handling
    const activitiesSubscription = supabase
      .channel('activities_realtime')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'activities' },
        (payload) => {
          console.log('📢 New activity received:', payload);
          
          if (payload.new) {
            setRecentActivities(prev => {
              // Check if activity already exists to avoid duplicates
              const exists = prev.some(activity => activity.id === payload.new.id);
              if (!exists) {
                console.log('➕ Adding new activity to state:', payload.new.action_type);
                return [payload.new, ...prev.slice(0, 49)];
              }
              return prev;
            });

            // Update last activity timestamp
            lastActivityRef.current = Date.now();
          }
        }
      )
      .subscribe((status) => {
        console.log('📢 Activities subscription status:', status);
      });

    // Subscribe to board changes for real-time board updates
    const boardsSubscription = supabase
      .channel('boards_realtime')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'boards' },
        (payload) => {
          console.log('📋 Board change detected:', payload);
          
          // Emit custom event for board changes
          window.dispatchEvent(new CustomEvent('boardUpdated', { 
            detail: { payload, type: payload.eventType } 
          }));
        }
      )
      .subscribe((status) => {
        console.log('📋 Boards subscription status:', status);
      });

    // Subscribe to ticket changes for real-time ticket updates
    const ticketsSubscription = supabase
      .channel('tickets_realtime')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'tickets' },
        (payload) => {
          console.log('🎫 Ticket change detected:', payload);
          
          // Emit custom event for ticket changes
          window.dispatchEvent(new CustomEvent('ticketUpdated', { 
            detail: { payload, type: payload.eventType } 
          }));
        }
      )
      .subscribe((status) => {
        console.log('🎫 Tickets subscription status:', status);
      });

    // Store subscriptions for cleanup
    cleanupRef.current = [
      sessionsSubscription, 
      activitiesSubscription, 
      boardsSubscription, 
      ticketsSubscription
    ];

    // Check subscription status and set connected state
    setTimeout(() => {
      const allConnected = cleanupRef.current.every(sub => 
        sub.state === 'SUBSCRIBED'
      );
      setIsConnected(allConnected);
      console.log('🔗 All subscriptions connected:', allConnected);
    }, 1000);

    // Initial data fetch
    fetchOnlineUsers();
    fetchRecentActivities();
  }, [fetchOnlineUsers, fetchRecentActivities]);

  const initializeRealTime = useCallback(async (user) => {
    if (!user) return;

    try {
      console.log('🚀 Initializing real-time collaboration for:', user.email);

      const sessionData = {
        user_id: user.id,
        user_email: user.email,
        user_name: user.user_metadata?.full_name || user.email,
        is_online: true,
        last_seen: new Date().toISOString(),
        current_board: null,
        cursor_position: { x: 0, y: 0 }
      };

      const { data: session, error } = await supabase
        .from('user_sessions')
        .upsert(sessionData, { onConflict: 'user_id' })
        .select()
        .single();

      if (error) throw error;

      sessionRef.current = session;
      setIsConnected(true);
      
      // Setup subscriptions
      setupSubscriptions();
      
      // Start heartbeat to maintain presence
      startHeartbeat();

      console.log('✅ Real-time collaboration initialized successfully');
    } catch (error) {
      console.error('❌ Error initializing real-time:', error);
      setIsConnected(false);
    }
  }, [setupSubscriptions, startHeartbeat]);

  const updateCursorPosition = useCallback(async (x, y) => {
    if (!sessionRef.current || !isConnected) return;

    try {
      // Throttle cursor updates to avoid overwhelming the database
      await supabase
        .from('user_sessions')
        .update({ 
          cursor_position: { x, y },
          last_seen: new Date().toISOString()
        })
        .eq('user_id', sessionRef.current.user_id);
    } catch (error) {
      console.error('Error updating cursor position:', error);
    }
  }, [isConnected]);

  const updateCurrentBoard = useCallback(async (boardName) => {
    if (!sessionRef.current || !isConnected) return;

    try {
      await supabase
        .from('user_sessions')
        .update({ 
          current_board: boardName,
          last_seen: new Date().toISOString()
        })
        .eq('user_id', sessionRef.current.user_id);

      // Update local session ref
      sessionRef.current = {
        ...sessionRef.current,
        current_board: boardName
      };
    } catch (error) {
      console.error('Error updating current board:', error);
    }
  }, [isConnected]);

  const logActivity = useCallback(async (actionType, details = {}) => {
    if (!sessionRef.current) {
      console.warn('⚠️ Cannot log activity: no active session');
      return;
    }

    try {
      const activity = {
        user_id: sessionRef.current.user_id,
        user_email: sessionRef.current.user_email,
        user_name: sessionRef.current.user_name,
        action_type: actionType,
        ticket_key: details.ticketKey || null,
        from_board: details.fromBoard || null,
        to_board: details.toBoard || null,
        details: details
      };

      const { data, error } = await supabase
        .from('activities')
        .insert(activity)
        .select()
        .single();

      if (error) throw error;

      console.log('📝 Activity logged successfully:', actionType, details);
      
      // Update local activities immediately for better UX
      setRecentActivities(prev => [data, ...prev.slice(0, 49)]);
      
    } catch (error) {
      console.error('❌ Error logging activity:', error);
    }
  }, []);

  const goOffline = useCallback(async () => {
    if (!sessionRef.current) return;

    try {
      console.log('📴 Going offline...');

      // Stop heartbeat
      stopHeartbeat();

      // Update session to offline
      await supabase
        .from('user_sessions')
        .update({ 
          is_online: false,
          last_seen: new Date().toISOString()
        })
        .eq('user_id', sessionRef.current.user_id);

      // Cleanup subscriptions
      cleanupRef.current.forEach(subscription => {
        if (subscription && typeof subscription.unsubscribe === 'function') {
          subscription.unsubscribe();
        }
      });

      // Reset state
      setIsConnected(false);
      setOnlineUsers([]);
      sessionRef.current = null;
      
      console.log('✅ Successfully went offline');
    } catch (error) {
      console.error('❌ Error going offline:', error);
    }
  }, [stopHeartbeat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      goOffline();
    };
  }, [goOffline]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('📱 Page hidden - pausing heartbeat');
        stopHeartbeat();
      } else {
        console.log('📱 Page visible - resuming heartbeat');
        if (sessionRef.current && isConnected) {
          startHeartbeat();
          sendHeartbeat(); // Send immediate heartbeat
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isConnected, startHeartbeat, stopHeartbeat, sendHeartbeat]);

  // Handle browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = () => {
      goOffline();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [goOffline]);

  return {
    onlineUsers,
    recentActivities,
    isConnected,
    initializeRealTime,
    updateCursorPosition,
    updateCurrentBoard,
    logActivity,
    goOffline,
    fetchOnlineUsers,
    fetchRecentActivities,
    sendHeartbeat
  };
}